import { forwardRef, ChangeEvent } from 'react'
import styled from 'styled-components'
import DefaultTooltip from 'components/Common/Tooltips/DefaultTooltip'
import { useTranslation } from 'react-i18next'

const Wrapper = styled.div`
  appearance: none;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  position: relative;
  height: 24px;
  width: 24px;
  cursor: pointer;

  :hover {
    background: rgba(18, 20, 50, 0.24);
    border-radius: 4px;
  }
`
const InputWrapper = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
`
const Input = styled.input`
  cursor: pointer;
  width: 100%;
  height: 100%;

  ::-webkit-file-upload-button {
    cursor: pointer;
  }
`
const Svg = styled.svg`
  pointer-events: auto;
`

type ReplaceButtonProps = {
  onChange?: (file: File | string | null) => void
  readOnly?: boolean
}

const ReplaceButton = forwardRef<HTMLInputElement, ReplaceButtonProps>(
  ({ onChange, readOnly }, ref) => {
    const { t } = useTranslation()
    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event?.target?.files?.[0]
      if (onChange && file) {
        onChange(file)
      }
    }

    return (
      <Wrapper>
        <DefaultTooltip
          tooltip={t('review.images.replace')}
          placement='top'
          offset={12}
          component={InputWrapper}
        >
          <Input
            ref={ref}
            type='file'
            accept='.jpeg, .png, .svg, .webp, .jpg, .heic'
            onChange={handleChange}
            readOnly={readOnly}
            onClick={e => {
              e.currentTarget.value = ''
            }}
          />
        </DefaultTooltip>
        <Svg
          xmlns='http://www.w3.org/2000/svg'
          width='14'
          height='16'
          viewBox='0 0 14 16'
          fill='none'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M13.0002 0.666016C13.3684 0.666016 13.6668 0.964492 13.6668 1.33268V5.33268C13.6668 5.70087 13.3684 5.99935 13.0002 5.99935H9.00017C8.63198 5.99935 8.3335 5.70087 8.3335 5.33268C8.3335 4.96449 8.63198 4.66602 9.00017 4.66602H12.3335V1.33268C12.3335 0.964492 12.632 0.666016 13.0002 0.666016Z'
            fill='white'
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M7.8226 2.73528C6.80577 2.57658 5.76473 2.71546 4.82506 3.13518C3.88539 3.5549 3.08723 4.23752 2.52684 5.10072C1.96645 5.96391 1.66777 6.97081 1.66683 7.99996C1.6665 8.36815 1.36775 8.66635 0.999561 8.66602C0.631371 8.66568 0.333167 8.36693 0.333502 7.99874C0.334674 6.71231 0.708019 5.45369 1.40851 4.37469C2.10899 3.2957 3.1067 2.44242 4.28129 1.91777C5.45587 1.39312 6.75717 1.21952 8.02821 1.41789C9.29926 1.61627 10.4858 2.17816 11.4446 3.03578L11.4461 3.03715L13.4461 4.83715C13.7198 5.08346 13.742 5.50499 13.4957 5.77866C13.2494 6.05233 12.8279 6.07452 12.5542 5.82821L10.5557 4.02959C10.5555 4.02937 10.5552 4.02915 10.555 4.02893C9.78804 3.3432 8.83912 2.89393 7.8226 2.73528Z'
            fill='white'
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M0.333502 10.666C0.333502 10.2978 0.631979 9.99935 1.00017 9.99935H5.00017C5.36836 9.99935 5.66683 10.2978 5.66683 10.666C5.66683 11.0342 5.36836 11.3327 5.00017 11.3327H1.66684V14.666C1.66684 15.0342 1.36836 15.3327 1.00017 15.3327C0.631979 15.3327 0.333502 15.0342 0.333502 14.666V10.666Z'
            fill='white'
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M13.0008 7.33268C13.369 7.33302 13.6672 7.63177 13.6668 7.99996C13.6657 9.28639 13.2923 10.545 12.5918 11.624C11.8913 12.703 10.8936 13.5563 9.71905 14.0809C8.54446 14.6056 7.24317 14.7792 5.97212 14.5808C4.70108 14.3824 3.51457 13.8205 2.55572 12.9629L2.55419 12.9615L0.554192 11.1615C0.280519 10.9152 0.258333 10.4937 0.504639 10.22C0.750945 9.94637 1.17247 9.92418 1.44614 10.1705L3.44461 11.9691C3.44487 11.9693 3.44512 11.9696 3.44537 11.9698C4.21232 12.6555 5.16123 13.1048 6.17773 13.2634C7.19457 13.4221 8.23561 13.2832 9.17527 12.8635C10.1149 12.4438 10.9131 11.7612 11.4735 10.898C12.0339 10.0348 12.3326 9.02789 12.3335 7.99874C12.3338 7.63055 12.6326 7.33235 13.0008 7.33268Z'
            fill='white'
          />
        </Svg>
      </Wrapper>
    )
  }
)

ReplaceButton.displayName = 'ReplaceButton'

export default ReplaceButton
